/** Formatting helpers shared across the portal. Plain language, no jargon. */

const integerFormatter = new Intl.NumberFormat('en-GB');

export function formatNumber(value: number): string {
  return integerFormatter.format(value);
}

/** e.g. 1 → "1 credit", 1250 → "1,250 credits", -3 → "-3 credits" */
export function formatCredits(value: number): string {
  return `${formatNumber(value)} ${Math.abs(value) === 1 ? 'credit' : 'credits'}`;
}

/** Signed, for ledger rows: 25 → "+25", -10 → "−10" (true minus sign). */
export function formatSignedCredits(value: number): string {
  if (value > 0) return `+${formatNumber(value)}`;
  return `−${formatNumber(Math.abs(value))}`;
}

/**
 * Minor units to a currency string: (15000, 'usd') → "$150.00"
 *
 * Formatted in `en-US` rather than the `en-GB` used for dates. The catalogue
 * is priced in USD (ruling P-1), and `en-GB` would render it "US$150.00" —
 * technically correct, but not what a customer being charged in dollars
 * expects to see next to a buy button.
 */
export function formatMoney(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountMinor / 100);
}

export function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '·';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * A plain day, formatted without ever becoming a `Date` - D-13.
 *
 * The sales view prints the boundaries of the period its totals cover, and
 * those are days rather than moments: "1 Jul 2026 to 30 Sep 2026". Parsing
 * `2026-07-01` into a `Date` makes it midnight UTC, and rendering that in a
 * browser west of Greenwich shows **30 June** — a VAT period whose printed
 * start date depends on where the reader is sitting.
 *
 * So the string is taken apart rather than parsed. Anything that is not a
 * `YYYY-MM-DD` day gets the same "·" every other formatter here gives to input
 * it cannot read.
 */
export function formatDay(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return '·';

  const [, year, month, dayOfMonth] = match as unknown as [string, string, string, string];
  const name = MONTH_NAMES[Number(month) - 1];
  if (!name) return '·';

  return `${String(Number(dayOfMonth))} ${name} ${year}`;
}

export function formatDateTime(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '·';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

const LEDGER_ENTRY_LABELS: Record<string, string> = {
  purchase: 'Credits bought',
  spend: 'Spent on an order',
  refund: 'Refund',
  expiry: 'Credits expired',
  monthly_grant: 'Monthly credits',
  adjustment: 'Manual adjustment',
};

/** Turns a ledger entry_type into something an agency owner can read. */
export function describeLedgerEntry(entryType: string): string {
  return LEDGER_ENTRY_LABELS[entryType] ?? entryType.replace(/_/g, ' ');
}
